import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Box, CircularProgress, Snackbar, Button, Alert } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

// Layouts
import MainLayout from './layouts/MainLayout';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CustomerList from './pages/customers/CustomerList';
import CustomerDetail from './pages/customers/CustomerDetail';
import CustomerForm from './pages/customers/CustomerForm';
import ContractList from './pages/contracts/ContractList';
import ContractDetail from './pages/contracts/ContractDetail';
import ContractForm from './pages/contracts/ContractForm';
import InvoiceList from './pages/invoices/InvoiceList';
import InvoiceDetail from './pages/invoices/InvoiceDetail';
import InvoiceForm from './pages/invoices/InvoiceForm';
import CreditNoteForm from './pages/invoices/CreditNoteForm';
import PaymentList from './pages/payments/PaymentList';
import TankList from './pages/tanks/TankList';
import RefillsPage from './pages/RefillsPage';
import ReadingsPage from './pages/ReadingsPage';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import RemindersPage from './pages/RemindersPage';
import AssistantPage from './pages/assistant/AssistantPage';

// Pagine Impianti
import { PlantList, PlantDetail, PlantForm } from './pages/plants';

// Pagine Contatori
import { MeterList, MeterDetail, MeterForm } from './pages/meters';

// Pagine Email
import Inbox from './pages/email/Inbox';
import EmailDetail from './pages/email/EmailDetail';
import ComposeEmail from './pages/email/ComposeEmail';
import EmailAccounts from './pages/email/EmailAccounts';
import AiDashboard from './pages/email/AiDashboard';

// Pagina Importazione
import ImportPage from './pages/ImportPage';
import LetturistiPage from './pages/LetturistiPage';

// Pagine WhatsApp
import WhatsappInbox from './pages/whatsapp/WhatsappInbox';
import WhatsappSetup from './pages/whatsapp/WhatsappSetup';
import PhoneMatching from './pages/whatsapp/PhoneMatching';

// Pagine Comunicazioni
import CommunicationList from './pages/communications/CommunicationList';
import CommunicationForm from './pages/communications/CommunicationForm';

// PWA Letturisti
import { PwaAuthProvider } from './pwa/PwaAuthContext';
import PwaLayout from './pwa/PwaLayout';
import PwaLoginPage from './pwa/pages/PwaLoginPage';
import PlantListPage from './pwa/pages/PlantListPage';
import PlantReadingPage from './pwa/pages/PlantReadingPage';
import SyncStatusPage from './pwa/pages/SyncStatusPage';

// Route protetta
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function UpdateNotification() {
  const [show, setShow] = useState(false);

  const handleUpdate = useCallback(() => {
    const reg = window.__swRegistration;
    if (reg && reg.waiting) {
      // Tell the waiting SW to activate
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      // Reload once the new SW takes over
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      }, { once: true });
    } else {
      // Fallback: hard reload
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    const handler = () => setShow(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  if (!show) return null;

  return (
    <Snackbar
      open={show}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ top: { xs: 8, sm: 24 } }}
    >
      <Alert
        severity="info"
        icon={<SystemUpdateAltIcon />}
        sx={{ width: '100%', alignItems: 'center', boxShadow: 6 }}
        action={
          <Button color="inherit" size="small" variant="outlined" onClick={handleUpdate}
            sx={{ fontWeight: 600, ml: 1 }}
          >
            Aggiorna
          </Button>
        }
      >
        Nuova versione disponibile!
      </Alert>
    </Snackbar>
  );
}

function App() {
  return (
    <>
    <UpdateNotification />
    <Routes>
      {/* Landing page pubblica */}
      <Route path="/" element={<LandingPage />} />

      {/* Login pubblico */}
      <Route path="/login" element={<Login />} />
      
      {/* Routes protette CRM sotto /crm */}
      <Route
        path="/crm"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route index element={<Dashboard />} />
        <Route path="assistant" element={<AssistantPage />} />
        
        {/* Clienti */}
        <Route path="clienti" element={<CustomerList />} />
        <Route path="clienti/nuovo" element={<CustomerForm />} />
        <Route path="clienti/:id" element={<CustomerDetail />} />
        <Route path="clienti/:id/modifica" element={<CustomerForm />} />
        
        {/* Contratti */}
        <Route path="contratti" element={<ContractList />} />
        <Route path="contratti/nuovo" element={<ContractForm />} />
        <Route path="contratti/:id" element={<ContractDetail />} />
        <Route path="contratti/:id/modifica" element={<ContractForm />} />
        
        {/* Impianti */}
        <Route path="impianti" element={<PlantList />} />
        <Route path="impianti/nuovo" element={<PlantForm />} />
        <Route path="impianti/:id" element={<PlantDetail />} />
        <Route path="impianti/:id/modifica" element={<PlantForm />} />
        
        {/* Contatori */}
        <Route path="contatori" element={<MeterList />} />
        <Route path="contatori/nuovo" element={<MeterForm />} />
        <Route path="contatori/:id" element={<MeterDetail />} />
        <Route path="contatori/:id/modifica" element={<MeterForm />} />
        
        {/* Fatture */}
        <Route path="fatture" element={<InvoiceList />} />
        <Route path="fatture/nuova" element={<InvoiceForm />} />
        <Route path="fatture/:id" element={<InvoiceDetail />} />
        <Route path="fatture/:id/modifica" element={<InvoiceForm />} />
          <Route path="fatture/:id/nota-credito" element={<CreditNoteForm />} />
        
        {/* Solleciti e Promemoria Pagamento */}
        <Route path="solleciti" element={<RemindersPage />} />
        
        {/* Pagamenti */}
        <Route path="pagamenti" element={<PaymentList />} />
        
        {/* Serbatoi */}
        <Route path="serbatoi" element={<TankList />} />
        
        {/* Rifornimenti */}
        <Route path="rifornimenti" element={<RefillsPage />} />
        
        {/* Letture e Fatturazione */}
        <Route path="letture" element={<ReadingsPage />} />
        
        {/* Email */}
        <Route path="email/inbox" element={<Inbox />} />
        <Route path="email/:id" element={<EmailDetail />} />
        <Route path="email/compose" element={<ComposeEmail />} />
        <Route path="email/accounts" element={<EmailAccounts />} />
        <Route path="email/ai-dashboard" element={<AiDashboard />} />
      
      {/* WhatsApp */}
      <Route path="whatsapp" element={<WhatsappInbox />} />
      <Route path="whatsapp/setup" element={<WhatsappSetup />} />
      <Route path="whatsapp/phone-matching" element={<PhoneMatching />} />

        {/* Comunicazioni */}
        <Route path="comunicazioni" element={<CommunicationList />} />
        <Route path="comunicazioni/nuova" element={<CommunicationForm />} />
        <Route path="comunicazioni/:id/modifica" element={<CommunicationForm />} />
        
        {/* Importazione Dati */}
        <Route path="importa" element={<ImportPage />} />

        {/* Gestione Letturisti */}
        <Route path="letturisti" element={<LetturistiPage />} />

        {/* Impostazioni */}
        <Route path="impostazioni" element={<Settings />} />
      </Route>
      
      {/* PWA Letturisti - route standalone */}
      <Route path="/letture/login" element={
        <PwaAuthProvider><PwaLoginPage /></PwaAuthProvider>
      } />
      <Route path="/letture" element={
        <PwaAuthProvider><PwaLayout /></PwaAuthProvider>
      }>
        <Route index element={<PlantListPage />} />
        <Route path="plant/:id" element={<PlantReadingPage />} />
        <Route path="sync" element={<SyncStatusPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default App;
